import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  CardActionArea,
  IconButton,
  Dialog,
  DialogContent,
} from '@mui/material';
import CustomerOptimizedSpreadsheet from './spreadsheets/CustomerOptimizedSpreadsheet';
import { createCustomerOrder, extractSheetData } from '../services/customerapi';
import CustomerItemsCacheService from '../services/customerItemsCacheService';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import ProcessingAnimation from './ProcessingAnimation';
import TableChartIcon from '@mui/icons-material/TableChart';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import DeleteIcon from '@mui/icons-material/Delete';

function CustomerOrderForm() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [allItems, setAllItems] = useState([]);
  const [formData, setFormData] = useState({
    order_number: '',
    notes: '',
    customer_notes: '',
    eta_type: 'asap',
    requested_eta_date: '',
    items: []
  });
  const [_spreadsheetData, _setSpreadsheetData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [_orderSummary, setOrderSummary] = useState(null);
  const [_isProcessing, setIsProcessing] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showProcessingDialog, setShowProcessingDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [_showMethodSelection, setShowMethodSelection] = useState(true);

  const steps = ['Order Details', 'Import Method', 'Items', 'Review & Submit'];

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const items = await CustomerItemsCacheService.getItems();
        setAllItems(items);
        // Set initial type from the first item
        if (items && items.length > 0) {
          const types = [...new Set(items.map(item => item.type))];
          setSelectedType(types[0]);
        }
      } catch (error) {
        toast.error('Failed to fetch items');
      }
    };
    fetchItems();
  }, []);

  const _handleSpreadsheetChange = ({ size, brim, shape, newValue }) => {
    if (!selectedType) return;

    const matchingItem = allItems.find(item => 
      item.type === selectedType && 
      item.size === size && 
      item.brim === brim && 
      item.shape === shape
    );

    if (!matchingItem) return;

    const numValue = parseInt(newValue) || 0;
    const newItems = [...formData.items];
    const existingItemIndex = newItems.findIndex(item => item.item_id === matchingItem.id);

    if (numValue > 0) {
      if (existingItemIndex >= 0) {
        newItems[existingItemIndex].quantity = numValue;
      } else {
        newItems.push({
          item_id: matchingItem.id,
          quantity: numValue,
          type: selectedType,
          size,
          brim,
          shape
        });
      }
    } else if (existingItemIndex >= 0) {
      newItems.splice(existingItemIndex, 1);
    }

    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateOrderDetails()) {
      return;
    }
    if (activeStep === 1) {
      setActiveStep(2);
      return;
    }
    if (activeStep === steps.length - 1) {
      handleSubmit();
      return;
    }
    
    if (activeStep === 2) {
      calculateOrderSummary();
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 2) {
      setSelectedMethod(null);
      setShowMethodSelection(true);
    }
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateOrderDetails = () => {
    if (formData.eta_type === 'date' && !formData.requested_eta_date) {
      toast.error('Please select a requested ETA date');
      return false;
    }
    return true;
  };

  const calculateOrderSummary = () => {
    const summary = {
      totalItems: formData.items.length,
      totalQuantity: formData.items.reduce((sum, item) => sum + item.quantity, 0),
      itemsByType: {},
    };

    formData.items.forEach(item => {
      const type = item.type || 'Unknown';
      if (!summary.itemsByType[type]) {
        summary.itemsByType[type] = {
          count: 0,
          quantity: 0,
          items: []
        };
      }
      summary.itemsByType[type].count++;
      summary.itemsByType[type].quantity += item.quantity;
      summary.itemsByType[type].items.push(item);
    });

    setOrderSummary(summary);
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setShowMethodSelection(false);
    setActiveStep(2);
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setShowProcessingDialog(true);
    
    // Fire and forget - don't wait for response
    createCustomerOrder({
      ...formData,
      is_customer_request: true
    }).catch(error => {
      console.error('Error submitting order request:', error);
      // Still proceed with success message since we don't want to wait for backend
    });
    
    // Show processing for 5 seconds
    setTimeout(() => {
      setShowProcessingDialog(false);
      setShowSuccessDialog(true);
      
      // Navigate after 2 more seconds
      setTimeout(() => {
        setShowSuccessDialog(false);
        navigate('/customer/dashboard');
      }, 2000);
      
      toast.success('Order request submitted! It will be reviewed by our team.');
    }, 5000);
  };

  const renderOrderDetails = () => (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Order Number (Optional)"
            name="order_number"
            value={formData.order_number}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              order_number: e.target.value
            }))}
            helperText="Leave blank for auto-generated number"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label="ETA Type"
            name="eta_type"
            value={formData.eta_type}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              eta_type: e.target.value
            }))}
            SelectProps={{
              native: true,
            }}
          >
            <option value="asap">As Soon As Possible</option>
            <option value="date">Specific Date</option>
          </TextField>
        </Grid>

        {formData.eta_type === 'date' && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Requested ETA Date"
              name="requested_eta_date"
              value={formData.requested_eta_date}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                requested_eta_date: e.target.value
              }))}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            name="customer_notes"
            value={formData.customer_notes}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              customer_notes: e.target.value
            }))}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const renderMethodSelection = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 3 
    }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Choose Order Creation Method
      </Typography>
      
      <Grid container spacing={3} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            <CardActionArea 
              onClick={() => handleMethodSelect('spreadsheet')}
              sx={{ height: '100%', p: 2 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <TableChartIcon sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Spreadsheet View
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Use an interactive spreadsheet to enter quantities by type
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            <CardActionArea 
              onClick={() => handleMethodSelect('sheet')}
              sx={{ height: '100%', p: 2 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <InsertLinkIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Import from Sheet
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Import order data directly from a Google Sheet
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSpreadsheetView = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {allItems && (
        <CustomerOptimizedSpreadsheet
          data={formData.items.reduce((acc, item) => {
            acc[item.item_id] = {
              quantity: item.quantity || 0
            };
            return acc;
          }, {})}
          metrics={['quantity']}
          onCellValueChanged={({ itemId, _metric, newValue }) => {
            const itemIndex = formData.items.findIndex(item => item.item_id === itemId);
            if (itemIndex !== -1) {
              const newItems = [...formData.items];
              newItems[itemIndex] = {
                ...newItems[itemIndex],
                quantity: newValue
              };
              setFormData(prev => ({
                ...prev,
                items: newItems
              }));
            } else if (newValue > 0) {
              setFormData(prev => ({
                ...prev,
                items: [
                  ...prev.items,
                  {
                    item_id: itemId,
                    quantity: newValue
                  }
                ]
              }));
            }
          }}
          title="Order Items"
          readOnly={false}
          fullScreen={true}
          hideZeros={true}
        />
      )}
    </Box>
  );

  const renderSheetImport = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', mt: 2, flex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <InsertLinkIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Import from Google Sheet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Enter the URL of your Google Sheet to import order data
          </Typography>
        </Box>

        <Box component="form" onSubmit={async (e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          const sheetUrl = formData.get('sheetUrl');

          if (!sheetUrl) {
            toast.error('Please enter a Google Sheet URL');
            return;
          }

          setIsLoading(true);
          try {
            const result = await extractSheetData(sheetUrl);
            if (result.success && result.data) {
              const newItems = result.data.map(item => ({
                item_id: item.itemId,
                quantity: item.value,
                type: item.type,
                size: item.size,
                brim: item.brim,
                shape: item.shape
              })).filter(item => item.quantity > 0);

              setFormData(prev => ({
                ...prev,
                items: newItems
              }));

              localStorage.setItem('lastUsedSheetUrl', sheetUrl);
              toast.success(`Successfully imported ${newItems.length} items from sheet`);
              
              // Calculate summary before moving to next step
              const summary = {
                totalItems: newItems.length,
                totalQuantity: newItems.reduce((sum, item) => sum + item.quantity, 0),
                itemsByType: {},
              };

              newItems.forEach(item => {
                const type = item.type || 'Unknown';
                if (!summary.itemsByType[type]) {
                  summary.itemsByType[type] = {
                    count: 0,
                    quantity: 0,
                    items: []
                  };
                }
                summary.itemsByType[type].count++;
                summary.itemsByType[type].quantity += item.quantity;
                summary.itemsByType[type].items.push(item);
              });

              setOrderSummary(summary);
              setActiveStep(3);
            }
          } catch (error) {
            toast.error(error.message || 'Failed to extract data from sheet');
          } finally {
            setIsLoading(false);
          }
        }}>
          <TextField
            fullWidth
            name="sheetUrl"
            label="Google Sheet URL"
            placeholder="https://docs.google.com/spreadsheets/d/..."
            required
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InsertLinkIcon sx={{ color: 'action.active', mr: 1 }} />
              ),
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? 'Processing...' : 'Import Data'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Important Notes:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Make sure the Google Sheet is shared with the appropriate permissions<br />
            • The sheet should follow the required format with item cells<br />
            • Only non-zero quantities will be imported<br />
            • Google Sheet should follow this template format: <a href="https://docs.google.com/spreadsheets/d/1TiBSZ4vO5Hdmk0p1nSZckIvCDyYq6b6P27DM6ejGx_c/" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>View Template</a>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );

  const renderOrderSummary = () => {
    const totalItems = formData.items.length;
    const totalQuantity = formData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    const sheetUrl = localStorage.getItem('lastUsedSheetUrl');
    const showSheetUrl = selectedMethod === 'sheet' && sheetUrl;

    // Map items with their details from allItems
    const itemsWithDetails = formData.items.map(item => {
      const itemDetails = allItems.find(i => i.id === item.item_id) || {};
      return {
        ...item,
        type_name: itemDetails.type_name || item.type || 'Unknown',
        size_name: itemDetails.size_name || item.size || 'Unknown',
        brim_name: itemDetails.brim_name || item.brim || 'Unknown',
        shape_name: itemDetails.shape_name || item.shape || 'Unknown',
        full_name: `${itemDetails.type_name || item.type || 'Unknown'} - ${itemDetails.size_name || item.size || ''} - ${itemDetails.brim_name || item.brim || ''} - ${itemDetails.shape_name || item.shape || ''}`.replace(/\s+-\s+$/, '')
      };
    });

    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto', mt: 2 }}>
        <Card>
          <Box sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
              >
                <CheckCircleIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              </motion.div>
              <Typography variant="h5" gutterBottom>
                Order Summary
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card className="surface-0 shadow-1">
                  <CardContent>
                    <Typography variant="h6" className="text-xl font-bold mb-3">
                      Order Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">Order Number</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.order_number || 'Auto-generated'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">ETA Type</Typography>
                        <Tag 
                          value={formData.eta_type === 'asap' ? 'ASAP' : 'Specific Date'}
                          severity={formData.eta_type === 'asap' ? 'info' : 'warning'}
                        />
                      </Box>
                      {formData.eta_type === 'date' && (
                        <>
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary">Requested Date</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {new Date(formData.requested_eta_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </>
                      )}
                      {formData.customer_notes && (
                        <>
                          <Divider />
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Notes</Typography>
                            <Typography variant="body2">
                              {formData.customer_notes}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>

                <Card className="surface-0 shadow-1" sx={{ mt: 3 }}>
                  <CardContent>
                    <Typography variant="h6" className="text-xl font-bold mb-3">
                      Order Statistics
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                        <Typography variant="h4" color="primary.contrastText">
                          {totalItems}
                        </Typography>
                        <Typography variant="body2" color="primary.contrastText">
                          Total Items
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 2 }}>
                        <Typography variant="h4" color="secondary.contrastText">
                          {totalQuantity}
                        </Typography>
                        <Typography variant="body2" color="secondary.contrastText">
                          Total Quantity
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {showSheetUrl && (
                  <Card className="surface-0 shadow-1" sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" className="text-xl font-bold mb-3">
                        Import Source
                      </Typography>
                      <Box sx={{ wordBreak: 'break-all' }}>
                        <Typography variant="subtitle2" color="text.secondary">Google Sheet URL</Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {sheetUrl}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className="surface-0 shadow-1">
                  <CardContent>
                    <Typography variant="h6" className="text-xl font-bold mb-3">
                      Order Items
                    </Typography>
                    <DataTable 
                      value={itemsWithDetails}
                      responsiveLayout="scroll"
                      stripedRows
                      showGridlines
                      size="small"
                      paginator
                      rows={10}
                      rowsPerPageOptions={[5, 10, 20, 50]}
                      emptyMessage="No items added to order"
                      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                      filterDisplay="menu"
                      globalFilterFields={['type_name', 'size_name', 'brim_name', 'shape_name', 'full_name']}
                    >
                      <Column 
                        field="full_name" 
                        header="Item" 
                        sortable 
                        filter
                        filterPlaceholder="Search items..."
                      />
                      <Column 
                        field="type_name" 
                        header="Type" 
                        sortable 
                        filter
                        filterPlaceholder="Search type..."
                        body={(rowData) => (
                          <Tag 
                            value={rowData.type_name}
                            severity="info"
                          />
                        )}
                      />
                      <Column 
                        field="size_name" 
                        header="Size" 
                        sortable 
                        filter
                        filterPlaceholder="Search size..."
                      />
                      <Column 
                        field="quantity" 
                        header="Quantity" 
                        sortable
                        body={(rowData) => (
                          <Tag 
                            value={rowData.quantity}
                            severity={rowData.quantity > 10 ? 'warning' : 'info'}
                          />
                        )}
                      />
                      <Column
                        header="Actions"
                        body={(rowData) => (
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveItem(formData.items.findIndex(item => item.item_id === rowData.item_id))}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                        style={{ width: '100px', textAlign: 'center' }}
                      />
                    </DataTable>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Box>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ mb: 4 }}>
          Create New Order Request
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <AnimatePresence mode="wait" custom={activeStep}>
          <motion.div
            key={activeStep}
            custom={activeStep}
            variants={{
              enter: (direction) => ({
                x: direction > 0 ? 1000 : -1000,
                opacity: 0
              }),
              center: {
                zIndex: 1,
                x: 0,
                opacity: 1
              },
              exit: (direction) => ({
                zIndex: 0,
                x: direction < 0 ? 1000 : -1000,
                opacity: 0
              })
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
          >
            {activeStep === 0 ? renderOrderDetails() :
             activeStep === 1 ? renderMethodSelection() :
             activeStep === 2 ? (
               selectedMethod === 'spreadsheet' ? renderSpreadsheetView() :
               renderSheetImport()
             ) : renderOrderSummary()}
          </motion.div>
        </AnimatePresence>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {activeStep > 0 && (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={activeStep === 2 && formData.items.length === 0}
          >
            {activeStep === steps.length - 1 ? 'Submit Order' : 'Next'}
          </Button>
        </Box>
      </Container>

      <ProcessingAnimation 
        open={showProcessingDialog}
        message="Submitting Your Order Request"
      />

      <Dialog 
        open={showSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 6 }}>
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ 
              type: "spring",
              stiffness: 260,
              damping: 20 
            }}
          >
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80,
                color: 'success.main',
                filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2))'
              }} 
            />
          </motion.div>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Typography variant="h5" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
              Order Request Submitted!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your order request has been received and will be reviewed by our team. You'll be notified once it's approved.
            </Typography>
          </motion.div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

export default CustomerOrderForm;